import { downloadResponseFile, fetchFile, QueryOptions } from '@shared/fetch-utils';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { useMPEFetch, useSelectedOrgUnitHeader } from '@retail/app/hooks';
import { TotalFileDownloadDTO, VilmaVersion } from '@retail/export/types';
import { buildUrl } from '@shared/utils';
import { mpeApi } from '@retail/mgpris/config';
import { useMutation } from '@tanstack/react-query';

export interface SuppliersSearchParams extends QueryOptions {
  assortmentId: number;
  from?: string;
}

export function useFetchTotalFileDownloadStatus({
  assortmentId,
  from,
  ...queryOptions
}: SuppliersSearchParams) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<TotalFileDownloadDTO>({
    method: 'GET',
    queryKey: ['totalFileSummary', from],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.export.summary.getFullLinkPath({
        assortmentId,
      }),
      searchParams: {
        from,
      },
    }),
    ...queryOptions,
  });
}

interface ExportTotalFileProps {
  assortmentId: number;
  supplierIds?: number[];
  downloadComplete?: boolean;
  vilmaVersion: VilmaVersion;
  from?: string;
  numberOfFutureDays?: number;
}

export const useFetchExportTotalFileZip = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();
  const selectedOrgUnitHeader = useSelectedOrgUnitHeader();

  return useMutation(
    ({
      assortmentId,
      supplierIds,
      downloadComplete,
      vilmaVersion,
      from,
      numberOfFutureDays,
    }: ExportTotalFileProps) =>
      fetchFile({
        token,
        url: buildUrl({
          base: mpe.url,
          route: mpeApi.export.downloadZip.getFullLinkPath({
            assortmentId,
          }),
          searchParams: {
            from,
            downloadComplete,
            'vilma-version': vilmaVersion,
            numberOfFutureDays,
          },
        }),
        body: supplierIds,
        method: 'POST',
        headers: selectedOrgUnitHeader,
      }),
    { onSuccess: downloadResponseFile({ filenameFallback: 'export.zip' }) }
  );
};

export const useFetchExportTotalFileCsv = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();
  const selectedOrgUnitHeader = useSelectedOrgUnitHeader();

  return useMutation(
    ({
      assortmentId,
      supplierIds,
      downloadComplete,
      vilmaVersion,
      from,
      numberOfFutureDays,
    }: ExportTotalFileProps) =>
      fetchFile({
        token,
        url: buildUrl({
          base: mpe.url,
          route: mpeApi.export.downloadCSV.getFullLinkPath({
            assortmentId,
          }),
          searchParams: {
            from,
            downloadComplete,
            'vilma-version': vilmaVersion,
            numberOfFutureDays,
          },
        }),
        body: supplierIds,
        method: 'POST',
        headers: selectedOrgUnitHeader,
      }),
    {
      onSuccess: downloadResponseFile({ filenameFallback: 'export.zip' }),
    }
  );
};
